import { FormEvent, useEffect, useRef, useState } from "react";
import type { IScannerControls } from "@zxing/browser";
import { isSupportedBarcode, lookupProduct, normalizeBarcode, type ProductMatch } from "../services/productLookup";
import "./BarcodeCapture.css";

type DetectorResult = { rawValue: string };
type Detector = { detect(source: HTMLVideoElement): Promise<DetectorResult[]> };
type DetectorConstructor = new (options: { formats: string[] }) => Detector;
type DecoderName = "native" | "ZXing";

const CAMERA_CONSTRAINTS: MediaStreamConstraints = {
  video: {
    facingMode: { ideal: "environment" },
    width: { ideal: 1280 },
    height: { ideal: 720 },
  },
  audio: false,
};

export function BarcodeCapture({ barcode, onBarcode, onProduct }: { barcode: string; onBarcode: (barcode: string) => void; onProduct: (product: ProductMatch) => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const timerRef = useRef<number | null>(null);
  const zxingControlsRef = useRef<IScannerControls | null>(null);
  const detectionLockedRef = useRef(false);
  const nativeDecodeBusyRef = useRef(false);
  const sessionRef = useRef(0);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [decoderName, setDecoderName] = useState<DecoderName | null>(null);
  const [lookupState, setLookupState] = useState<"idle" | "loading" | "found" | "missing" | "error">("idle");
  const [message, setMessage] = useState("");
  const [cameraMessage, setCameraMessage] = useState("");

  const stopCamera = () => {
    sessionRef.current += 1;
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
    timerRef.current = null;
    zxingControlsRef.current?.stop();
    zxingControlsRef.current = null;
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
    if (videoRef.current) videoRef.current.srcObject = null;
    nativeDecodeBusyRef.current = false;
    setCameraOpen(false);
    setDecoderName(null);
  };
  useEffect(() => stopCamera, []);

  const runLookup = async (value: string) => {
    const clean = normalizeBarcode(value);
    onBarcode(clean);
    setLookupState("loading");
    setMessage("Looking up product…");
    try {
      const product = await lookupProduct(clean);
      if (product) { onProduct(product); setLookupState("found"); setMessage(`Found ${product.name || "product"}. Check the details below.`); }
      else { setLookupState("missing"); setMessage("No product found. Keep the barcode and enter the product name below."); }
    } catch (problem) {
      setLookupState("error");
      setMessage(problem instanceof Error ? problem.message : "Lookup failed. You can still enter the item manually.");
    }
  };

  const acceptDetection = (rawValue: string) => {
    const value = normalizeBarcode(rawValue);
    if (!isSupportedBarcode(value) || detectionLockedRef.current) return;
    detectionLockedRef.current = true;
    stopCamera();
    void runLookup(value);
  };

  const startNativeDecoder = (video: HTMLVideoElement, BarcodeDetector: DetectorConstructor, session: number) => {
    const detector = new BarcodeDetector({ formats: ["ean_8", "ean_13", "upc_a", "upc_e"] });
    setDecoderName("native");
    timerRef.current = window.setInterval(async () => {
      if (session !== sessionRef.current || detectionLockedRef.current || nativeDecodeBusyRef.current || video.readyState < 2) return;
      nativeDecodeBusyRef.current = true;
      try {
        const result = await detector.detect(video);
        acceptDetection(result[0]?.rawValue || "");
      } catch { /* An undecodable frame is expected; keep scanning. */ }
      finally { nativeDecodeBusyRef.current = false; }
    }, 300);
  };

  const startZxingDecoder = async (video: HTMLVideoElement, stream: MediaStream, session: number) => {
    const { BarcodeFormat, BrowserMultiFormatReader } = await import("@zxing/browser");
    if (session !== sessionRef.current) return;
    const reader = new BrowserMultiFormatReader(undefined, { delayBetweenScanAttempts: 180, delayBetweenScanSuccess: 500 });
    reader.possibleFormats = [BarcodeFormat.UPC_A, BarcodeFormat.UPC_E, BarcodeFormat.EAN_8, BarcodeFormat.EAN_13];
    setDecoderName("ZXing");
    const controls = await reader.decodeFromStream(stream, video, (result) => {
      if (session === sessionRef.current && result) acceptDetection(result.getText());
    });
    if (session !== sessionRef.current) controls.stop();
    else zxingControlsRef.current = controls;
  };

  useEffect(() => {
    if (!cameraOpen) return;
    const video = videoRef.current;
    const stream = streamRef.current;
    const session = sessionRef.current;
    if (!video || !stream) return;

    video.srcObject = stream;
    let cancelled = false;
    void video.play().then(async () => {
      if (cancelled || session !== sessionRef.current) return;
      const BarcodeDetector = (window as unknown as { BarcodeDetector?: DetectorConstructor }).BarcodeDetector;
      try {
        if (BarcodeDetector) {
          try { startNativeDecoder(video, BarcodeDetector, session); }
          catch { await startZxingDecoder(video, stream, session); }
        } else await startZxingDecoder(video, stream, session);
      } catch {
        if (!cancelled && session === sessionRef.current) {
          setCameraMessage("The barcode decoder could not start. Close the camera and try again, or enter the barcode below.");
          stopCamera();
        }
      }
    }).catch(() => {
      if (!cancelled && session === sessionRef.current) {
        setCameraMessage("The live camera preview could not start. Enter the barcode below instead.");
        stopCamera();
      }
    });

    return () => { cancelled = true; };
  }, [cameraOpen]);

  const startCamera = async () => {
    setCameraMessage("");
    if (!window.isSecureContext) { setCameraMessage("Camera scanning requires HTTPS. Open FoodKeep from its secure web address, or enter the barcode below."); return; }
    if (!navigator.mediaDevices?.getUserMedia) { setCameraMessage("Camera access is not supported here. Enter the barcode below."); return; }
    detectionLockedRef.current = false;
    const session = sessionRef.current + 1;
    sessionRef.current = session;
    try {
      const stream = await navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS);
      if (session !== sessionRef.current) { stream.getTracks().forEach((track) => track.stop()); return; }
      streamRef.current = stream;
      setCameraOpen(true);
    } catch (problem) {
      const name = problem instanceof DOMException ? problem.name : "";
      const cameraError = name === "NotAllowedError" || name === "SecurityError"
        ? "Camera permission was denied. Allow camera access in Safari or browser settings, then try again."
        : name === "NotFoundError" || name === "OverconstrainedError"
          ? "No usable rear camera was found. Enter the barcode below instead."
          : "The camera could not start. Close other apps using it, try again, or enter the barcode below.";
      setCameraMessage(cameraError);
      stopCamera();
    }
  };

  const submit = (event: FormEvent) => { event.preventDefault(); void runLookup(barcode); };
  return <section className="barcode-capture full" aria-labelledby="barcode-heading">
    <div className="barcode-heading"><div><span className="eyebrow">Barcode Capture Beta</span><h2 id="barcode-heading">Scan or enter a barcode</h2></div>{cameraOpen ? <button type="button" onClick={stopCamera}>Close camera</button> : <button type="button" className="scan-button" onClick={() => void startCamera()}>▣ Use camera</button>}</div>
    {cameraOpen && <div className="scanner-viewport"><video ref={videoRef} playsInline muted autoPlay aria-label="Barcode camera preview"/><div className="scanner-guide" aria-hidden="true"/><p>Hold the UPC/EAN inside the frame{decoderName ? ` · ${decoderName}` : ""}</p></div>}
    {cameraMessage && <p className="scanner-message" role="alert">{cameraMessage}</p>}
    <div className="manual-barcode" role="group" aria-label="Manual barcode entry"><label>Barcode<input inputMode="numeric" autoComplete="off" value={barcode} onChange={(event) => { onBarcode(normalizeBarcode(event.target.value)); setLookupState("idle"); setMessage(""); }} placeholder="8, 12, 13, or 14 digits" aria-describedby="barcode-help"/></label><button type="button" className="primary-button" disabled={!isSupportedBarcode(barcode) || lookupState === "loading"} onClick={submit}>{lookupState === "loading" ? "Looking up…" : "Look up"}</button></div>
    <small id="barcode-help">Manual entry is always available. The barcode will be saved even when no product match is found.</small>
    {message && <p className={`lookup-message ${lookupState}`} role="status">{lookupState === "loading" && <span className="mini-spinner" aria-hidden="true"/>}{message}</p>}
  </section>;
}
